import { ReactNode } from 'react'

import { useIncidentDescription } from '../../../../features/incident-drawer/shared/hooks/useIncidentDescription'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { Box, Flex } from '@chakra-ui/react'

import { IncidentCreatorType } from '@/features/incident-drawer'
import { IncidentType } from '@/graphql/generated/schemas'
import { DeviceType } from '@/graphql/generated/schemas'
import { IncidentTypeSlug } from '@/utils/incidents'

import { ButtonTag } from '../../ButtonTag'
import { DeviceIcon } from './DeviceIcon'
import { FloorIcon } from './FloorIcon'
import { ManualIncidentDescription } from './ManualIncidentDescription'

interface IncidentDescriptionIProps {
  type: IncidentType | null
  isManualIncident?: boolean
  description?: string
  floorName?: string
  floorId?: string
  deviceName?: string
  deviceType?: DeviceType
  deviceId?: string
  showButtonTag?: boolean
  creator?: IncidentCreatorType
  manualIncidentCategoryName?: string
  name?: string
  slug?: string | null
}

export const IncidentDescription = ({
  type,
  isManualIncident = false,
  creator,
  description,
  floorName,
  floorId,
  deviceName,
  deviceType,
  deviceId,
  showButtonTag = true,
  manualIncidentCategoryName,
  name,
  slug = null,
}: IncidentDescriptionIProps) => {
  const isIncidentTypeDefinition = useFeatureFlag(
    FeatureFlag.useIncidentTypeDefinition
  )
  const { message, onDeviceClick, onFloorClick } = useIncidentDescription({
    deviceId,
    deviceType,
    floorId,
    type,
  })

  const deviceFloorDescription = (
    <Box
      as='span'
      mt='5px'
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        rowGap: '2px',
      }}
    >
      <Box as='span' mr='1' verticalAlign='middle'>
        {`${name} was created from `}
      </Box>
      <IncidentTag
        icon={<DeviceIcon type={deviceType} />}
        name={deviceName}
        onClick={onDeviceClick}
        // No drawer for this device type, so the tag renders as plain text.
        showButtonTag={showButtonTag && !!onDeviceClick}
      />
      <Box as='span' ml='1' mr='1' verticalAlign='middle'>
        on
      </Box>
      <IncidentTag
        icon={<FloorIcon />}
        name={floorName}
        onClick={onFloorClick}
        showButtonTag={showButtonTag}
      />
    </Box>
  )

  // Manual incidents render their own summary/creator/category regardless of
  // slug. Checking the slug-based ITD branch first would make this branch
  // unreachable as soon as a manual incident also carries an ITD slug — a
  // real gap once ITDs started covering guard-created manual incidents too.
  if (isManualIncident) {
    return (
      <ManualIncidentDescription
        creator={creator}
        description={description}
        manualIncidentCategoryName={manualIncidentCategoryName}
      />
    )
  }

  if (isIncidentTypeDefinition && slug != null) {
    if (slug === IncidentTypeSlug.DoorUnlockedTailgate) {
      return (
        <Flex direction='column'>
          <span>{message}</span>
        </Flex>
      )
    }
    return deviceFloorDescription
  }

  switch (type) {
    case IncidentType.Tailgating:
      return (
        <Flex direction='column'>
          <span>{message}</span>
        </Flex>
      )
    default:
      return deviceFloorDescription
  }
}

interface IncidentTagIProps {
  icon: ReactNode
  name: string
  showButtonTag: boolean
  /** Undefined renders the tag as plain text — see `showButtonTag`. */
  onClick?: () => void
}

export const IncidentTag = ({
  icon,
  name,
  showButtonTag,
  onClick,
}: IncidentTagIProps) => {
  if (showButtonTag) {
    return (
      <Box
        alignItems='center'
        as='span'
        display='inline-flex'
        margin='2px'
        verticalAlign='middle'
      >
        <ButtonTag onClick={onClick}>
          {icon}
          {name}
        </ButtonTag>
      </Box>
    )
  }
  return (
    <Box
      alignItems='center'
      as='span'
      display='inline-flex'
      verticalAlign='middle'
    >
      {icon}
      <Box as='span' verticalAlign='middle'>
        {name}
      </Box>
    </Box>
  )
}
